/**
 * apps/command/src/components/AlertFeed.tsx — §16 screen 8's queue, P1 subset.
 *
 * §9.1's exception-first framing, as a component: worst and oldest first, and
 * an explicit calm "nothing is wrong" state rather than an empty panel. An
 * empty box and a healthy fleet look identical, and only one of them is good
 * news.
 *
 * ============================================================================
 * ACKNOWLEDGING IS THE ONLY WRITE, AND IT IS OPTIMISTIC.
 * ============================================================================
 *
 * §15.9: "optimistic updates — an action reflects instantly and reconciles on
 * server confirmation; a failure rolls back VISIBLY with the reason, never a
 * silent revert." An operator working a queue during an incident presses this
 * button many times in a minute; a round trip between each press and its effect
 * is what makes a console feel like a form.
 *
 * Resolving is deliberately NOT here. §4.4 requires a resolution note — "what
 * was actually done" — and a one-click resolve in a feed is how a queue gets
 * emptied without anything being fixed. It belongs on the alert's own screen,
 * with the note, in P1's remaining work.
 *
 * A RULE NAME THE DICTIONARY DOES NOT KNOW RENDERS AS THE SLUG. §4.4 says the
 * thresholds are editable in Settings, so a deployment may raise a rule this
 * build has never heard of — and `kiosk_thermal_shutdown` on screen is legible,
 * while a blank row is a bug report.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';
import { hasKey, t } from '../i18n/index.js';
import { ageLabel } from '../lib/format.js';
import { Button, EmptyState, Notice, Panel, PanelHeader, Skeleton } from '../ui/index.jsx';

type MbtAlert = components['schemas']['MbtAlert'];

/**
 * §4.4's rule name → copy, falling back to the slug.
 *
 * `hasKey` rather than comparing `t()`'s output to the key it was given: the
 * fallback decision is "does this dictionary know the rule", and asking that
 * question directly also lets `t()` keep its template literal inline — which is
 * what `npm run mbt:check` reads to know these keys are used.
 */
function ruleLabel(rule: string): string {
  return hasKey(`alert.rule.${rule}`) ? t(`alert.rule.${rule}`) : rule;
}

export function AlertFeed({ onExpired }: { onExpired: () => void }): React.JSX.Element {
  const [alerts, setAlerts] = useState<MbtAlert[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(timer);
  }, []);

  const load = useCallback(async () => {
    try {
      const data = unwrap(await api().GET('/mbt/fleet/alerts'));
      setAlerts(data.items as MbtAlert[]);
      setFailed(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) onExpired();
      // The list already on screen is NOT cleared. It stays true as of when it
      // arrived, which during an incident is worth far more than a blank panel.
      else setFailed(true);
    }
  }, [onExpired]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 15_000);
    return () => clearInterval(timer);
  }, [load]);

  const acknowledge = useCallback(
    async (alert: MbtAlert) => {
      const stamp = new Date().toISOString();
      // Optimistic. The rollback below restores the exact previous row rather
      // than re-fetching, so a failure cannot also lose an update that landed
      // between the press and the error.
      const previous = alerts;
      setAlerts(
        (current) =>
          current?.map((item) =>
            item.id === alert.id ? { ...item, acknowledged_at: stamp } : item,
          ) ?? current,
      );
      try {
        await api().PATCH('/mbt/fleet/alerts/{alert_id}', {
          params: { path: { alert_id: alert.id } },
          body: { acknowledge: true },
        });
        void load();
      } catch (error) {
        setAlerts(previous);
        if (error instanceof ApiError && error.status === 401) onExpired();
        else setFailed(true);
      }
    },
    [alerts, load, onExpired],
  );

  return (
    <Panel>
      <PanelHeader
        title={t('alert.title')}
        aside={alerts ? String(alerts.length) : undefined}
      />
      <div className="p-4">
        {failed ? <Notice tone="warn">{t('error.network')}</Notice> : null}

        {alerts === null ? (
          <div className="space-y-2">
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
          </div>
        ) : alerts.length === 0 ? (
          // §9.1 — an explicit, calm "everything normal", never a blank panel.
          <EmptyState message={t('alert.empty')} />
        ) : (
          <ul className="divide-y divide-hairline">
            {alerts.map((alert) => (
              <li key={alert.id} className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-label">
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                        alert.severity === 'critical' ? 'bg-crit' : 'bg-warn'
                      }`}
                      aria-hidden="true"
                    />
                    <span className="font-medium tabular-nums text-ink-100">
                      {alert.short_code}
                    </span>
                    <span className="truncate text-ink-300">{ruleLabel(alert.rule)}</span>
                  </p>
                  <p className="mt-0.5 pl-3.5 text-micro tabular-nums text-ink-500">
                    {t('alert.since', { at: ageLabel(new Date(alert.opened_at), now, t) })}
                    {alert.acknowledged_by_name
                      ? ` · ${t('alert.acknowledged', { name: alert.acknowledged_by_name })}`
                      : ''}
                  </p>
                </div>
                <span className="flex shrink-0 items-center gap-2">
                  {alert.acknowledged_at ? null : (
                    <Button tone="quiet" onClick={() => void acknowledge(alert)}>
                      {t('alert.acknowledge')}
                    </Button>
                  )}
                  {/* §15.9's drill-down: the feed states the fact, the screen
                      is where it can be interrogated and closed. */}
                  <Link
                    to={`/alerts?id=${alert.id}`}
                    className="text-micro text-ink-500 transition-colors duration-state hover:text-ink-300"
                  >
                    {t('alert.detail')}
                  </Link>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
